// Turns legs and day items into one time-ordered agenda in the trip timezone.
import type { DayItem, Leg, LegKind } from '../../shared/pack/schema'
import { legBooked } from '../../shared/trip'
import { dateZone, tripZone, type ZoneTrip } from '../../shared/tripZone'
import { instantOf, zoned } from './time'

export interface AgendaItem {
  id: string
  /** Trip-timezone date the item sits on. */
  date: string
  startMs: number
  endMs?: number
  title: string
  sub?: string
  booked: boolean
  source: 'leg' | 'item'
  legKind?: LegKind
  placeId?: string
  /** A plan with no time (it sorts to the end of its day). */
  untimed?: true
}

/** The trip's zone, or (B13) the trip itself, whose stages may each have their own. */
type Zone = string | ZoneTrip
const zoneAt = (tz: Zone, ms: number) => (typeof tz === 'string' ? tz : tripZone(tz, ms))
const zoneOn = (tz: Zone, date: string) => (typeof tz === 'string' ? tz : dateZone(tz, date))

export function legToAgenda(leg: Leg, tz: Zone): AgendaItem {
  const startMs = Date.parse(leg.departsAt)
  return {
    id: `leg:${leg.id}`,
    date: zoned(startMs, zoneAt(tz, startMs)).date,
    startMs,
    endMs: Date.parse(leg.arrivesAt),
    title: `${leg.from.name} → ${leg.to.name}`,
    ...(leg.ref ? { sub: leg.ref } : {}),
    // Only a booked journey is "booked" (a leg with no flag counts when it has a booking ref).
    booked: legBooked(leg),
    source: 'leg',
    legKind: leg.kind,
    ...(leg.untimed ? { untimed: true as const } : {}),
  }
}

export function dayItemToAgenda(item: DayItem, tz: Zone): AgendaItem {
  // A plan's time is wall-clock time where its day's stage is (B13).
  const zone = zoneOn(tz, item.date)
  // Untimed items sort to the end of their day.
  const startMs = instantOf(item.date, item.time ?? '23:59', zone)
  return {
    id: `item:${item.id}`,
    date: item.date,
    startMs,
    ...(item.endTime ? { endMs: instantOf(item.date, item.endTime, zone) } : {}),
    title: item.title,
    ...(item.sub ? { sub: item.sub } : {}),
    booked: item.booked,
    source: 'item',
    ...(item.placeId ? { placeId: item.placeId } : {}),
    ...(item.time ? {} : { untimed: true as const }),
  }
}

export function buildAgenda(legs: Leg[], items: DayItem[], tz: Zone): AgendaItem[] {
  return [...legs.map((l) => legToAgenda(l, tz)), ...items.map((i) => dayItemToAgenda(i, tz))].sort(
    (a, b) => a.startMs - b.startMs,
  )
}
